import axios from 'axios';
import { getCharacterPrompt } from '../data/ai/characterPrompts';

const OPENROUTER_API_KEY = (import.meta.env.VITE_OPENROUTER_API_KEY || '').trim();

// ✅ 2026年5月 最新穩定免費模型（依穩定度排序）
const FALLBACK_MODELS = [
  'google/gemini-2.5-flash-preview:free',
  'google/gemini-2.0-flash-001:free',
  'deepseek/deepseek-chat-v3-0324:free',
  'deepseek/deepseek-r1:free',
  'meta-llama/llama-4-maverick:free',
  'meta-llama/llama-4-scout:free',
  'meta-llama/llama-3.3-70b-instruct:free',
  'qwen/qwen3-235b-a22b:free',
  'qwen/qwen3-30b-a3b:free',
  'qwen/qwen3-14b:free',
  'microsoft/phi-4-reasoning:free',
  'mistralai/mistral-7b-instruct:free',
  'google/gemma-3-27b-it:free',
];

// 主力模型（2026年5月最穩定）
const AI_MODEL_PRIMARY = 'google/gemini-2.5-flash-preview:free';

const isValidApiKey = (key) => {
  if (!key || key.length < 20) return false;
  const placeholders = ['your_', 'xxxxx', 'your-key', 'placeholder', 'here'];
  if (placeholders.some(p => key.toLowerCase().includes(p))) return false;
  return key.startsWith('sk-');
};

const AI_PROVIDER = isValidApiKey(OPENROUTER_API_KEY) ? 'openrouter' : 'mock';

if (AI_PROVIDER === 'openrouter') {
  console.log('✅ OpenRouter API Key 已設定，使用真實 AI');
} else {
  console.warn('⚠️ 未設定有效的 OpenRouter API Key，使用模擬模式');
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const simulateResponse = (character, chatHistory, userName) => {
  return new Promise(resolve => {
    setTimeout(() => {
      const lastMsg = chatHistory[chatHistory.length - 1]?.content || '';
      const nameGreet = userName ? `${userName}，` : '';

      if (lastMsg.includes('抽到了塔羅牌【')) {
        resolve(
          `(燭光微微搖晃，占卜師凝視著你抽出的牌)\n\n${nameGreet}這張牌出現得正是時候。它映照出了你內心深處那些尚未被承認的情緒。\n\n牌面的指引已經很清晰了，但我想問你：當你看到這張牌的第一眼，心裡浮現的是誰的影子？`
        );
      } else {
        const responses = [
          `(${character.name}靜靜地看著你)\n\n${nameGreet}我聽到了。你說「${lastMsg.substring(0, 20)}...」，這的確不容易。給自己一點時間去消化這些感受吧，我在這裡陪著你。`,
          `${nameGreet}(輕輕點頭) 嗯......你說的這些，讓我想多了解一些。能告訴我，這件事是從什麼時候開始困擾你的嗎？`,
          `(沉默片刻)\n\n${nameGreet}有時候，把話說出口本身就是一種勇氣。你願意告訴我，已經很了不起了。`,
        ];
        resolve(responses[Math.floor(Math.random() * responses.length)]);
      }
    }, 1500 + Math.random() * 1000);
  });
};

const makeOpenRouterRequest = async (modelToUse, normalizedMessages, promptConfig) => {
  const response = await axios.post(
    'https://openrouter.ai/api/v1/chat/completions',
    {
      model: modelToUse,
      messages: normalizedMessages,
      temperature: Number(promptConfig.temperature) || 0.7,
      max_tokens: Number(promptConfig.max_tokens) || 1000,
    },
    {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENROUTER_API_KEY}`,
        'HTTP-Referer': window.location.origin,
        'X-Title': 'Midnight Bar',
      },
      timeout: 30000,
    }
  );

  if (!response.data?.choices?.length) {
    throw new Error('API 回傳格式錯誤或為空');
  }

  const content = response.data.choices[0].message?.content;
  if (!content || content.trim() === '') {
    throw new Error('模型回傳空內容');
  }

  return content;
};

const normalizeMessages = (chatHistory, systemPrompt) => {
  let historyToSend = chatHistory.map(msg => ({
    role: msg.role === 'ai' ? 'assistant' : 'user',
    content: msg.content || '(無言)',
  }));

  if (historyToSend.length === 0 || historyToSend[0].role === 'assistant') {
    historyToSend.unshift({ role: 'user', content: '(推門走進酒吧，坐了下來)' });
  }

  const normalized = [];
  let lastRole = null;
  for (const msg of historyToSend) {
    if (msg.role === lastRole) {
      normalized[normalized.length - 1].content += '\n\n' + msg.content;
    } else {
      normalized.push({ ...msg });
      lastRole = msg.role;
    }
  }

  if (normalized.length > 0 && normalized[0].role === 'user') {
    normalized[0].content = `[System Instructions]\n${systemPrompt}\n[End System Instructions]\n\n${normalized[0].content}`;
  }

  return normalized;
};

const callOpenRouter = async (character, chatHistory, userName) => {
  const promptConfig = getCharacterPrompt(character.id);

  let systemPrompt = promptConfig.systemPrompt;
  if (userName) {
    systemPrompt += `\n\n<使用者資訊>\n對方的名字是「${userName}」。請在對話中自然地稱呼對方的名字。\n</使用者資訊>`;
  }

  const normalizedMessages = normalizeMessages(chatHistory, systemPrompt);

  // 主力模型優先，備用清單隨機打散
  const shuffled = [...FALLBACK_MODELS].sort(() => Math.random() - 0.5);
  const modelsToTry = [...new Set([AI_MODEL_PRIMARY, ...shuffled])].slice(0, 7);

  console.log(`🚀 [2026.05] 本次嘗試順序:`, modelsToTry);

  for (let i = 0; i < modelsToTry.length; i++) {
    const model = modelsToTry[i];
    try {
      console.log(`🤖 [${i + 1}/${modelsToTry.length}] 嘗試: ${model}`);
      const result = await makeOpenRouterRequest(model, normalizedMessages, promptConfig);
      console.log(`✅ 成功！使用模型: ${model}`);
      return result;
    } catch (error) {
      const status = error.response?.status;
      const errMsg = error.response?.data?.error?.message || error.message;
      console.warn(`❌ [${i + 1}] ${model} 失敗 (${status || 'Error'}): ${errMsg}`);

      if (i < modelsToTry.length - 1) {
        const delay = status === 429 ? 3000 : 1000;
        console.log(`⏳ 等待 ${delay}ms 後換下一個...`);
        await sleep(delay);
      }
    }
  }

  throw new Error('所有模型均失敗');
};

export const chatService = {
  sendMessage: async (character, chatHistory, userName = null) => {
    try {
      if (AI_PROVIDER === 'openrouter') {
        return await callOpenRouter(character, chatHistory, userName);
      }
      return await simulateResponse(character, chatHistory, userName);
    } catch (error) {
      console.error('❌ 所有模型均失敗，降級為模擬模式:', error);

      const status = error.response?.status;

      if (status === 401) {
        return '(系統提示：API Key 無效或已過期，請至 openrouter.ai 確認)';
      }

      const simRes = await simulateResponse(character, chatHistory, userName);
      return `(系統提示：免費 AI 伺服器目前壅塞，已自動切換為離線模式。建議稍後再試。)\n\n---\n\n${simRes}`;
    }
  },
};